'use client'

import {useCallback, useState, type ComponentType} from 'react'
import {Clock3, ExternalLink, Globe2, Mail, MapPin, MessageSquare, Phone} from 'lucide-react'
import {useTranslation} from '../../../hooks/useTranslation'
import {cn} from '../../../lib/utils'
import type {PublicPartnerOpeningHour, PublicPartnerProfile} from '../../../services/public-profile.service'
import {PublicPartnerLogo} from '../shared/PublicPartnerLogo'
import {FacebookIcon, InstagramIcon} from '../../ui/icons'
import {SanitizedRichContent} from '../../ui/SanitizedRichContent'
import {PUBLIC_SITE_CONTENT_CLASS_NAME, PublicSiteShell} from './PublicSiteShell'
import {publicEntryStyles} from './publicEntryStyles'
import {hasPublicProfileDetails} from './publicProfileVisibility'
import type {DateTimeFormatter} from '@fitro/date-time'

type PublicAboutStudioPageProps = {
    partnerName: string
    profile?: PublicPartnerProfile | null
}

type ContactItem = {
    href: string
    icon: ComponentType<{className?: string}>
    label: string
}

type OpeningHoursRow = {
    days: string
    value: string
}

type MapDetails = {
    embedUrl: string | null
    href: string | null
}

const OSM_ZOOM = 17
const OSM_BBOX_LAT_DELTA = 0.006
const OSM_BBOX_LNG_DELTA = 0.01

export function PublicAboutStudioPage({partnerName, profile}: PublicAboutStudioPageProps) {
    const {getTranslationWithFallback, t, dateTime} = useTranslation('landing')
    const logoUrl = profile?.logoUrl ?? null
    const [logoVisibility, setLogoVisibility] = useState<{isVisible: boolean; src: string | null}>({
        isVisible: Boolean(logoUrl),
        src: logoUrl,
    })
    const displayName = profile?.displayName?.trim() || partnerName
    const descriptionHtml = profile?.descriptionHtml?.trim() ?? ''
    const address = profile ? formatAddress(profile) : null
    const directions = profile?.address.directions?.trim() ?? ''
    const mapDetails = profile ? buildMapDetails(profile) : {embedUrl: null, href: null}
    const contactItems = profile ? buildContactItems(profile) : []
    const openingHoursRows = profile
        ? buildOpeningHoursRows(profile.openingHours, getTranslationWithFallback, dateTime)
        : []
    const hasLocationDetails = Boolean(mapDetails.embedUrl || address || directions || mapDetails.href)
    const hasVisibleLogo = Boolean(
        logoUrl && (logoVisibility.src === logoUrl ? logoVisibility.isVisible : true)
    )
    const hasPublicDetails = hasPublicProfileDetails(profile)

    const handleLogoVisibilityChange = useCallback((isVisible: boolean) => {
        setLogoVisibility((current) => {
            if (current.src === logoUrl && current.isVisible === isVisible) return current
            return {isVisible, src: logoUrl}
        })
    }, [logoUrl])

    return (
        <PublicSiteShell
            activePage="about"
            partnerName={partnerName}
            showAboutPage={hasPublicDetails}
        >
            <div className={cn(PUBLIC_SITE_CONTENT_CLASS_NAME, publicEntryStyles.pagePaddingLoose)}>
                <section aria-labelledby="public-about-title">
                    <h1 id="public-about-title" className="sr-only">{displayName}</h1>

                    <article className="min-w-0" data-testid="public-about-description">
                        {logoUrl ? (
                            <PublicPartnerLogo
                                src={logoUrl}
                                onVisibilityChange={handleLogoVisibilityChange}
                            />
                        ) : null}
                        {descriptionHtml ? (
                            <SanitizedRichContent
                                html={descriptionHtml}
                                className={cn(hasVisibleLogo ? 'mt-5' : '', 'max-w-4xl text-[15px] font-normal leading-7 text-[var(--landing-neutral-600)] md:text-[16px]')}
                            />
                        ) : null}
                    </article>

                    <div className="mt-8 grid gap-8 md:grid-cols-[minmax(0,0.9fr)_minmax(300px,0.75fr)] lg:grid-cols-[minmax(0,0.85fr)_minmax(360px,0.8fr)] lg:gap-12">
                        <div className="min-w-0 space-y-7 md:order-2">
                            {hasLocationDetails ? (
                                <section className="space-y-4" aria-labelledby="public-about-location">
                                    <div className="flex items-center gap-2 text-[14px] font-semibold leading-[1.4] text-[var(--landing-neutral-900)]">
                                        <MapPin className="h-4 w-4 text-[var(--landing-primary-600)]" aria-hidden="true" />
                                        <h2 id="public-about-location">{t('publicEntry.about.locationTitle')}</h2>
                                    </div>
                                    {mapDetails.embedUrl ? (
                                        <div className="overflow-hidden rounded-[12px] bg-slate-100 shadow-[inset_0_0_0_1px_rgba(226,232,240,0.92)]">
                                            <iframe
                                                className="h-[180px] w-full border-0 md:h-[220px]"
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                                src={mapDetails.embedUrl}
                                                title={t('publicEntry.about.mapTitle')}
                                            />
                                        </div>
                                    ) : null}
                                    {address ? (
                                        <div className="text-[15px] leading-6 text-[var(--landing-neutral-700)]">
                                            <span className="block font-medium text-[var(--landing-neutral-900)]">{t('publicEntry.about.addressLabel')}</span>
                                            <span>{address}</span>
                                        </div>
                                    ) : null}
                                    {directions ? (
                                        <div className="text-[14px] leading-6 text-[var(--landing-neutral-500)]">
                                            <span className="block font-medium text-[var(--landing-neutral-700)]">{t('publicEntry.about.directionsLabel')}</span>
                                            <span>{directions}</span>
                                        </div>
                                    ) : null}
                                    {mapDetails.href ? (
                                        <a
                                            className="inline-flex items-center gap-2 text-[14px] font-medium leading-none text-[var(--landing-primary-600)] transition-colors hover:text-[var(--landing-primary-700)]"
                                            href={mapDetails.href}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            {t('publicEntry.about.openMap')}
                                            <ExternalLink className="h-4 w-4" aria-hidden="true" />
                                        </a>
                                    ) : null}
                                </section>
                            ) : null}
                        </div>

                        <aside className="min-w-0 space-y-7 md:order-1">
                            {contactItems.length > 0 ? (
                                <section aria-labelledby="public-about-contact">
                                    <div className="mb-3 flex items-center gap-2 text-[14px] font-semibold leading-[1.4] text-[var(--landing-neutral-900)]">
                                        <MessageSquare className="h-4 w-4 text-[var(--landing-primary-600)]" aria-hidden="true" />
                                        <h2 id="public-about-contact">{t('publicEntry.about.contactTitle')}</h2>
                                    </div>
                                    <div className="divide-y divide-slate-100 border-y border-slate-100">
                                        {contactItems.map((item) => {
                                            const Icon = item.icon
                                            return (
                                                <a
                                                    key={`${item.href}-${item.label}`}
                                                    className="flex min-w-0 items-center gap-3 py-3 text-[14px] leading-[1.4] text-[var(--landing-neutral-600)] transition-colors hover:text-[var(--landing-primary-600)]"
                                                    href={item.href}
                                                    rel={item.href.startsWith('http') ? 'noreferrer' : undefined}
                                                    target={item.href.startsWith('http') ? '_blank' : undefined}
                                                >
                                                    <Icon className="h-4 w-4 shrink-0 text-[var(--landing-primary-600)]" />
                                                    <span className="min-w-0 break-words">{item.label}</span>
                                                </a>
                                            )
                                        })}
                                    </div>
                                </section>
                            ) : null}

                            {openingHoursRows.length > 0 ? (
                                <section aria-labelledby="public-about-opening-hours">
                                    <div className="mb-3 flex items-center gap-2 text-[14px] font-semibold leading-[1.4] text-[var(--landing-neutral-900)]">
                                        <Clock3 className="h-4 w-4 text-[var(--landing-primary-600)]" aria-hidden="true" />
                                        <h2 id="public-about-opening-hours">{t('publicEntry.about.openingHoursTitle')}</h2>
                                    </div>
                                    <div className="divide-y divide-slate-100 border-y border-slate-100">
                                        {openingHoursRows.map((row) => (
                                            <div key={`${row.days}-${row.value}`} className="grid grid-cols-[minmax(84px,0.8fr)_minmax(0,1fr)] gap-4 py-3 text-[14px] leading-[1.4]">
                                                <span className="font-medium text-[var(--landing-neutral-900)]">{row.days}</span>
                                                <span className="text-[var(--landing-neutral-600)]">{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                        </aside>
                    </div>
                </section>
            </div>
        </PublicSiteShell>
    )
}

function formatAddress(profile: PublicPartnerProfile): string | null {
    const cityLine = [profile.address.postalCode, profile.address.city].filter(Boolean).join(' ')
    return [profile.address.street, cityLine].filter(Boolean).join(', ') || null
}

function buildMapDetails(profile: PublicPartnerProfile): MapDetails {
    const {latitude, longitude} = profile.address
    const hasCoordinates = typeof latitude === 'number' && Number.isFinite(latitude)
        && typeof longitude === 'number' && Number.isFinite(longitude)

    if (hasCoordinates) {
        const params = new URLSearchParams({
            bbox: [
                longitude - OSM_BBOX_LNG_DELTA,
                latitude - OSM_BBOX_LAT_DELTA,
                longitude + OSM_BBOX_LNG_DELTA,
                latitude + OSM_BBOX_LAT_DELTA,
            ].join(','),
            layer: 'mapnik',
            marker: `${latitude},${longitude}`,
        })

        return {
            embedUrl: `https://www.openstreetmap.org/export/embed.html?${params.toString()}`,
            href: `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=${OSM_ZOOM}/${latitude}/${longitude}`,
        }
    }

    return {embedUrl: null, href: null}
}

function buildContactItems(profile: PublicPartnerProfile): ContactItem[] {
    const items: ContactItem[] = []

    if (profile.contact.phone) {
        items.push({
            href: `tel:${profile.contact.phone.replace(/\s+/g, '')}`,
            icon: Phone,
            label: profile.contact.phone,
        })
    }

    if (profile.contact.email) {
        items.push({
            href: `mailto:${profile.contact.email}`,
            icon: Mail,
            label: profile.contact.email,
        })
    }

    if (profile.contact.websiteUrl) {
        items.push({
            href: normalizeExternalHref(profile.contact.websiteUrl),
            icon: Globe2,
            label: profile.contact.websiteUrl,
        })
    }

    if (profile.socialLinks.instagramUrl) {
        items.push({
            href: normalizeExternalHref(profile.socialLinks.instagramUrl),
            icon: InstagramIcon,
            label: 'Instagram',
        })
    }

    if (profile.socialLinks.facebookUrl) {
        items.push({
            href: normalizeExternalHref(profile.socialLinks.facebookUrl),
            icon: FacebookIcon,
            label: 'Facebook',
        })
    }

    return items
}

function normalizeExternalHref(value: string): string {
    return /^https?:\/\//i.test(value) ? value : `https://${value}`
}

function buildOpeningHoursRows(
    openingHours: PublicPartnerOpeningHour[],
    getTranslationWithFallback: (key: string, fallback: string, params?: Record<string, string>) => string,
    dateTime: DateTimeFormatter
): OpeningHoursRow[] {
    if (openingHours.length === 0) return []

    const closedLabel = getTranslationWithFallback('publicEntry.about.closed', 'Zamknięte')
    const valueByDay = new Map<number, string>()
    for (let day = 1; day <= 7; day += 1) {
        const workWindows = openingHours
            .filter((item) => item.dayOfWeek === day && (!item.type || item.type === 'work'))
            .sort((left, right) => left.startTime.localeCompare(right.startTime))

        valueByDay.set(
            day,
            workWindows.length > 0
                ? formatOpeningWindows(workWindows, dateTime)
                : closedLabel
        )
    }

    const rows: OpeningHoursRow[] = []
    let rangeStart = 1
    let currentValue = valueByDay.get(1) ?? closedLabel

    for (let day = 2; day <= 8; day += 1) {
        const nextValue = valueByDay.get(day)
        if (day <= 7 && nextValue === currentValue) continue

        rows.push({
            days: dateTime.formatWeekdayRange(rangeStart, day - 1),
            value: currentValue,
        })
        rangeStart = day
        currentValue = nextValue ?? currentValue
    }

    return rows
}

function formatOpeningWindows(
    windows: PublicPartnerOpeningHour[],
    dateTime: DateTimeFormatter
): string {
    return windows
        .slice()
        .sort((left, right) => left.startTime.localeCompare(right.startTime))
        .map((window) => dateTime.formatTimeRange(window.startTime, window.endTime))
        .join(', ')
}
